// import { pricing } from 'components/Pricing/pricingData';
import {
	TDownloadVatReportsFileFormat,
	TSalesReportTypes,
	TVatReportTypes,
} from 'redux/reducers/reports/types';
import { TExchangeRateRequestPayload } from 'redux/reducers/vatCorrections/types';

export const endpoint = {
	auth: {
		LOGOUT: '/api2/auth/open-id/logout',
		CONNECT: '/api2/auth/open-id/connect',
		REFRESH_TOKEN: '/api2/auth/refresh/token',
	},
	dashboard: {
		CACHE_REFRESH: '/api2/cache/refresh',
		DASHBOARD_SERVICES: '/api2/general/dashboard_services',
	},
	user: {
		ORGANIZATIONS: '/api2/organizations',
		USER_INFO: '/api2/auth/userinfo',
		GET_NEW_MESSAGE_COUNT: '/api2/messages/get_new_message_count',
		FETCH_MESSAGES: '/api2/messages/fetch_messages',
		MARK_AS_READ_MESSAGE: (messageId: number) => `/api2/messages/mark_as_read_message/${messageId}`,
		DELETE_MESSAGE: (messageId: number) => `/api2/messages/delete_message/${messageId}`,
		UPDATE_SETTINGS: '/api2/emerchants/settings',
		EM_ASSIGNMENT: '/api2/emerchants/assignment',
		CONTRACT_DOCUMENTS: (emId: number) => `/api2/emerchants/${emId}/attachments`,
		CONTRACT_DOCUMENT_DOWNLOAD: (emId: number, attachmentId: number) =>
			`/api2/emerchants/${emId}/attachments/${attachmentId}`,
		FETCH_USERS: '/api2/user/fetch_users',
		UPDATE_USER: '/api2/user/update_dashboard_user',
		DELETE_USER: (username: string) => `/api2/user/delete_dashboard_user/${username}`,
		CREATE_DASHBOARD_USER: '/api2/user/create_dashboard_user',
		UPDATE_NOTIFICATION_SUBSCRIPTION: '/api2/user/update_notification_subscription',
		FETCH_NOTIFICATION_SUBSCRIPTIONS: (email: string) =>
			`/api2/user/fetch_notification_subscriptions/${email}`,
		CREDENTIALS_RESET: '/api2/auth/user/credentials/reset',
		TWO_FA_STATUS: '/api2/auth/open-id/2fa/status',
		TWO_FA_ENABLE: '/api2/auth/open-id/2fa/enable',
		TWO_FA_DISABLE: '/api2/auth/open-id/2fa/disable',
		SPECIAL_SCHEMES: '/api2/user/special_schemes',
		INTEGRATIONS: '/api2/integrations',
		NEW_WIX_INTEGRATION: '/api2/integrations/wix',
		NEW_SQUARESPACE_INTEGRATION: '/api2/integrations/squarespace',
		NEW_STRIPE_INTEGRATION: '/api2/integrations/stripe',
		UPLOADED_SALES_REPORTS: '/api2/em_sales_reports/uploaded_files',
		UPLOAD_SALES_REPORT: '/api2/em_sales_reports/upload_file',
		DOWNLOAD_SALES_REPORT: (fileId: number) => `/api2/em_sales_reports/download_file/${fileId}`,
		SUPPORTED_COUNTRY_LIST: '/api2/user/supported_country_list',
	},
	orders: {
		LOAD_VAT_AND_EAS_FEE: '/api2/orders/vat_and_eas_fee',
		LOAD_CONFIRMED_ORDERS: '/api2/orders/confirmed',
		ORDER_DETAILS_COMBINED: (orderId: string) => `/api2/orders/order_details/${orderId}`,
		CREATE_ORDER_RETURN: '/api2/orders/create_order_return',
		CRATE_ORDER_DISCOUNT: '/api2/orders/create_order_discount',
	},
	refunds: {
		LOAD_REFUNDS: '/api2/orders/returns',
	},
	emExports: {
		EXPORT_ORDERS: '/api2/em_exports/orders',
		EXPORT_RETURNS: '/api2/em_exports/returns',
		DOWNLOAD_FILE: (hash: string) => `/api2/em_exports/file/${hash}`,
	},
	ordersSync: {
		SHOPIFY: (orderId: number) => `/api2/orders/${orderId}/sync/shopify`,
		WIX: (orderId: number) => `/api2/orders/${orderId}/sync/wix`,
		SHOPWARE: (orderId: number) => `/api2/orders/${orderId}/sync/shopware`,
	},
	invoices: {
		INVOICES: '/api2/invoice/list',
		FETCH_PAYMENT_LINK: (invoiceNumber: number) => `/api2/invoice/${invoiceNumber}/payment_link`,
		DOWNLOAD_INVOICE_PDF: (invoiceId: number) => `/api2/invoice/${invoiceId}/pdf`,
		DOWNLOAD_INVOICE_CSV: (invoiceId: number) => `/api2/invoice/${invoiceId}/invoice_udr`,
		LAST_INVOICE_STATUS: '/api2/invoice/last_invoice_status',
	},
	reports: {
		GET_AVAILABLE_REPORT_TYPES: '/api2/em_vat_reports/available_report_types',
		GET_SALES_REPORTS: (reportType: TSalesReportTypes) =>
			`/api2/em_vat_reports/standard/${reportType as string}`,
		DOWNLOAD_SALES_REPORTS: (reportId: number, format: TDownloadVatReportsFileFormat) =>
			`/api2/em_vat_reports/standard/${reportId}/${format as string}`,
		GET_VAT_REPORTS: (reportType: TVatReportTypes) =>
			`/api2/em_vat_reports/fiscal/${reportType as string}`,
		DOWNLOAD_VAT_REPORT: (
			reportType: TVatReportTypes,
			reportId: number,
			format: TDownloadVatReportsFileFormat,
		) => `/api2/em_vat_reports/fiscal/${reportType as string}/${reportId}/${format as string}`,
	},
	vatCorrections: {
		GET_VAT_CORRECTIONS: '/api2/vat_corrections',
		GET_SYSTEM_TIME: '/api2/general/system_time',
		GET_CURRENCIES: '/api2/currency/supported',
		// LOAD_EU_COUNTRIES: '/api/loadeucountries',
		GET_EXCHANGE_RATE: (payload: TExchangeRateRequestPayload) =>
			`/api2/exchange_rate/${payload.month}/${payload.year}/${payload.currency}`,
		GET_TO_GBP_EXCHANGE_RATE: (payload: TExchangeRateRequestPayload) =>
			`/api2/exchange_rate/gbp/${payload.month}/${payload.year}/${payload.currency}`,
		VAT_CORRECTIONS_REQUEST: '/api2/vat_corrections/vat_return_request',
		VAT_CORRECTIONS_DOWNLOAD: 'api2/vat_corrections/file',
		GET_ORDER_AVAILABLE_CORRECTION_AMOUNT:
			'/api2/vat_corrections/get_order_available_correction_amount',
	},
	// Payment gateway (httpPay)
	paymentMethods: {
		LIST: '/api/payment_methods',
		SUMMARY: '/api/payment_methods/summary',
		SERVICES: '/api/payment_services',
		METHOD_SERVICES: (id: number) => `/api/payment_methods/${id}/services`,
		DEFAULT: (id: number) => `/api/payment_methods/${id}/default`,
		DELETE: (id: number) => `/api/payment_methods/${id}`,
		SETUP: '/api/payment_methods/setup',
		SETUP_GET: (setupId: string) => `/api/payment_methods/setup/${setupId}`,
		SETUP_COMPLETE: (setupId: string) => `/api/payment_methods/setup/${setupId}/complete`,
		// operator restrictions for the client: adding/changing payment methods, Make payment
		CLIENT_ACCESS: '/api/client-access',
	},
	directDebit: {
		BANKS: '/api/direct_debit/available_banks',
		CREATE: '/api/direct_debit/create',
		VERIFY: '/api/direct_debit/verify',
	},
	payments: {
		PAYABLE: '/api/invoices/payable',
		// receipts of invoices paid online: ?invoiceIds=1&invoiceIds=2 (the gateway binds repeated keys, not invoiceIds[])
		INVOICE_RECEIPTS: (invoiceIds: number[]) =>
			`/api/invoices/receipts?${invoiceIds.map((id) => `invoiceIds=${encodeURIComponent(id)}`).join('&')}`,
		QUOTE: '/api/payments/quote',
		TRY_TO_PAY: '/api/payments/try_to_pay',
		STATUS: (transactionId: string) => `/api/payments/${transactionId}`,
	},
	transactions: {
		GET_TRANSACTIONS: '/api/transactions',
	},
	hubspot: {
		VISITOR_TOKEN: '/api2/hubspot/visitor_token',
	},
	pricing: {
		AVAILABLE_PLANS: '/api2/pricing/available_plans',
		CURRENT_PLAN: '/api2/pricing/current_plan',
		REQUEST_NEW_PLAN: '/api2/pricing/request_new_plan',
		REQUESTED_PLAN: '/api2/pricing/requested_plan',
	},
	swissPost: {
		GET_STATUS: '/api2/swiss_post/status',
		GET_ORDERS: '/api2/swiss_post/orders',
		GET_ORDER: (orderId: number) => `/api2/swiss_post/orders/${orderId}`,
		REQUEST_DELIVERY: (orderId: number) => `/api2/swiss_post/orders/${orderId}/request_delivery`,
		DOWNLOAD_PROFORMA_INVOICE: (orderId: number) =>
			`/api2/swiss_post/orders/${orderId}/proforma-invoice`,
		SIGNED_PROFORMA_INVOICE_PREVIEW: (orderId: number) =>
			`/api2/swiss_post/orders/${orderId}/proforma-invoice/signed-preview`,
		UPLOAD_PROFORMA_INVOICE: (orderId: number) =>
			`/api2/swiss_post/orders/${orderId}/upload_proforma_invoice`,
		DOWNLOAD_LABEL: (orderId: number) => `/api2/swiss_post/orders/${orderId}/label`,
		UPDATE_HS6_CODE: (orderId: number) => `/api2/swiss_post/orders/${orderId}/update_hs6_code`,
		UPDATE_WEIGHT: (orderId: number) => `/api2/swiss_post/orders/${orderId}/update_weight`,
		HS_CODE_DESCRIPTION: '/api2/swiss_post/hs_code_description',
		UPDATE_ORDER_FIELD_VALUE: (orderId: number) =>
			`/api2/swiss_post/orders/${orderId}/update_order_field_value`,
		UPDATE_CUSTOMS_DESCRIPTION: (orderId: number) =>
			`/api2/swiss_post/orders/${orderId}/update_description`,
		FIRST_MILE_LABEL: (orderId: number) => `/api2/swiss_post/orders/${orderId}/first-mile-label`,
		SEND_LAST_ERROR_BY_EMAIL: (orderId: number) =>
			`/api2/swiss_post/orders/${orderId}/send_last_error_by_email`,
	},
	hmrc: {
		GET_HMRC_OAUTH_URL: '/api2/hmrc/oauth_url',
		SET_HMRC_OAUTH_URL: '/api2/hmrc',
		DELETE_HMRC_OAUTH_URL: '/api2/hmrc',
		CHECK_AVAILABILITY: '/api2/hmrc/check_availability',
	},
	gpsr: {
		GET_STATUS: '/api2/gpsr/status',
		GET_AVAILABLE_PRODUCT_TYPES: '/api2/gpsr/available_product_types',
		UPDATE_DESCRIPTION: (productTypeId: number) => `/api2/gpsr/${productTypeId}/update_description`,
		GET_SKUS: (productTypeId: number) => `/api2/gpsr/${productTypeId}/skus`,
		DELETE_SKUS: (productTypeId: number, skuId: number) =>
			`/api2/gpsr/${productTypeId}/skus/${skuId}`,
		ATTACH_SKUS: (productTypeId: number) => `/api2/gpsr/${productTypeId}/attach_sku`,
		GENERATE_DOC: (productTypeId: number) => `/api2/gpsr/${productTypeId}/generate_doc`,
		UPLOAD_DOC: (productTypeId: number) => `/api2/gpsr/${productTypeId}/upload_doc`,
		GET_DOC: (productTypeId: number) => `/api2/gpsr/${productTypeId}/doc`,
		UPLOAD_TECH_FILE: (productTypeId: number) => `/api2/gpsr/${productTypeId}/upload_tech_file`,
		GET_TECH_FILE: (productTypeId: number) => `/api2/gpsr/${productTypeId}/tech_file`,
		ADDITIONAL_INFORMATION: '/api2/gpsr/additional_information',
		CONFIRM_ADDITIONAL_INFORMATION: '/api2/gpsr/confirm_additional_information',
		CANCELLATION: '/api2/gpsr/cancellation',
		CHANGE_SERVICE_REQUEST: '/api2/gpsr/change_service_request',
		PLANS: '/api2/gpsr/plans',
	},
	euDdp: {
		GET_ORDERS: '/api2/eu_ddp/orders',
		GET_ORDER: (orderId: number) => `/api2/eu_ddp/orders/${orderId}`,
		GET_ORDER_STATUSES: '/api2/eu_ddp/order_statuses',
		REQUEST_SHIPMENT: (orderId: number) => `/api2/eu_ddp/orders/${orderId}/request_shipment`,
		CONFIRM_SHIPMENT: (orderId: number) => `/api2/eu_ddp/orders/${orderId}/confirm_shipment`,
		UPDATE_WEIGHT: (orderId: number) => `/api2/eu_ddp/orders/${orderId}/update_weight`,
		UPDATE_DESCRIPTION: (orderId: number) => `/api2/eu_ddp/orders/${orderId}/update_description`,
		UPDATE_HS6_CODE: (orderId: number) => `/api2/eu_ddp/orders/${orderId}/update_hs6_code`,
		UPDATE_ORDER_FIELD_VALUE: (orderId: number) =>
			`/api2/eu_ddp/orders/${orderId}/update_order_field_value`,
		PROFORMA_INVOICE: (orderId: number) => `/api2/eu_ddp/orders/${orderId}/proforma-invoice`,
		SIGNED_PROFORMA_INVOICE: (orderId: number) =>
			`/api2/eu_ddp/orders/${orderId}/signed-proforma-invoice`,
		UPLOAD_PROFORMA_INOICE: (orderId: number) =>
			`/api2/eu_ddp/orders/${orderId}/upload_proforma_invoice`,
		LABEL: (orderId: number) => `/api2/eu_ddp/orders/${orderId}/label`,
		DELIVERY_OPERATORS: '/api2/eu_ddp/delivery_operators',
		S10: (orderId: number) => `/api2/eu_ddp/orders/${orderId}/s10`,
	},
	eudr: {
		GET_STATUS: '/api2/eudr/status',
		GET_PLANS: '/api2/eudr/plans',
		GET_INFORMATION: '/api2/eudr/information',
		UPLOAD_FILE: '/api2/eudr/upload_file',
		DOWNLOAD_FILE: (id: number) => `/api2/eudr/download/${id}`,
		UPLOADED_FILES: '/api2/eudr/uploaded_files',
	},
	ppwr: {
		PACKING_TYPES: '/api2/ppwr/packing_types',
		PACKING_TYPE_WEIGHT: (packingTypeId: number) =>
			`/api2/ppwr/packing_types/${packingTypeId}/weight`,
		PACKING_TYPE_ENABLED: (packingTypeId: number) =>
			`/api2/ppwr/packing_types/${packingTypeId}/enabled`,
		UPLOAD_PROOF_DOCUMENT: (packingTypeId: number) =>
			`/api2/ppwr/packing_types/${packingTypeId}/proof_documents`,
		PROOF_DOCUMENT: (documentId: number) =>
			`/api2/ppwr/packing_types/proof_documents/${documentId}`,
		COUNTRY_EXCLUSIONS: '/api2/ppwr/country_exclusions',
		COUNTRY_EXCLUSION: (id: number) => `/api2/ppwr/country_exclusions/${id}`,
		MANUAL_REPORTING: '/api2/ppwr/manual_reporting',
		MANUAL_REPORTING_UPSERT: '/api2/ppwr/manual_reporting/upsert',
	},
	shopifyApps: {
		ADD_APP: '/api2/shopify/apps',
		SEARCH_APPS: '/api2/shopify/apps/search',
		INSTALL_STORE: (appId: number) => `/api2/shopify/apps/${appId}/stores`,
		SEARCH_STORES: (appId: number) => `/api2/shopify/apps/${appId}/stores/search`,
	},
	zendesk: {
		ZENDESK_USER_TOKEN: '/api2/zendesk/user_token',
	},
	country: {
		ALL: '/api2/country/all',
		LOAD_EU_COUNTRIES: '/api2/country/eu',
	},
	publicAttachment: {
		GET_UPLOAD_INFO: (hash: string) => `/api2/emerchants/attachments/public/upload/${hash}`,
		UPLOAD_FILE: (hash: string) => `/api2/emerchants/attachments/public/upload/${hash}`,
	},
};
